import type { CSSProperties, ReactNode } from "react"
import { Icon } from "../Icon"
import { Badge } from "../ui"

type SegmenterLike = new (
  locales?: string | string[],
  options?: { granularity: "grapheme" }
) => { segment(value: string): Iterable<{ segment: string }> }

function iconParts(value: string) {
  const Segmenter = (Intl as unknown as { Segmenter?: SegmenterLike }).Segmenter
  const parts = Segmenter
    ? [...new Segmenter("ru", { granularity: "grapheme" }).segment(value)].map((item) => item.segment)
    : Array.from(value)
  return parts.filter((part) => part.trim()).slice(0, 9)
}

export function EmojiIcon({ value, className = "" }: { value: string; className?: string }) {
  const parts = iconParts(value)
  return (
    <span className={`${className} emoji-cluster emoji-cluster--${Math.max(1, parts.length)}`} aria-label={value}>
      {(parts.length ? parts : ["🪙"]).map((part, index) => (
        <span aria-hidden="true" key={`${part}:${index}`}>
          {part}
        </span>
      ))}
    </span>
  )
}

export function CurrencyAmount({
  amount,
  symbol,
  name,
  signed = false
}: {
  amount: string | number
  symbol: string
  name?: string
  signed?: boolean
}) {
  const numeric = typeof amount === "number" ? amount : Number(amount)
  const formatted = Number.isFinite(numeric) ? new Intl.NumberFormat("ru-RU").format(numeric) : String(amount)
  return (
    <span className="currency-amount">
      <span aria-hidden="true">{symbol}</span>
      <strong>
        {signed && numeric > 0 ? "+" : ""}
        {formatted}
      </strong>
      {name ? <small>{name}</small> : null}
    </span>
  )
}

export function CurrencyCard({
  name,
  symbol,
  icon,
  amount,
  color,
  restricted = false,
  actions
}: {
  name: string
  symbol: string
  icon: string
  amount: string | number
  color?: string
  restricted?: boolean
  actions?: ReactNode
}) {
  return (
    <article className="family-currency-card" style={{ "--domain-color": color } as CSSProperties}>
      <div className="entity-identity">
        <EmojiIcon className="entity-icon" value={icon} />
        <div>
          <h3>{name}</h3>
          {restricted ? (
            <small>
              <Icon name="lock" size={13} /> Личный баланс
            </small>
          ) : (
            <small>Семейная валюта</small>
          )}
        </div>
      </div>
      <CurrencyAmount amount={amount} symbol={symbol} />
      {actions ? <div className="entity-actions">{actions}</div> : null}
    </article>
  )
}

export function RewardPreview({ items }: { items: Array<{ icon: string; label: string; amount?: string | number }> }) {
  return (
    <div className="reward-preview">
      {items.map((item, index) => (
        <span key={`${item.label}:${index}`}>
          <i>{item.icon}</i>
          <strong>
            {item.amount ? `${item.amount} ` : ""}
            {item.label}
          </strong>
        </span>
      ))}
    </div>
  )
}

function ProductCard({
  icon,
  title,
  description,
  meta,
  accent,
  actions,
  className = ""
}: {
  icon: string
  title: string
  description?: string | undefined
  meta?: ReactNode
  accent?: string | undefined
  actions?: ReactNode
  className?: string | undefined
}) {
  return (
    <article className={`product-card ${className}`} style={{ "--domain-color": accent } as CSSProperties}>
      <EmojiIcon className="product-card__icon" value={icon} />
      <div className="product-card__copy">
        <h3>{title}</h3>
        {description ? <p>{description}</p> : null}
      </div>
      {meta ? <div className="product-card__meta">{meta}</div> : null}
      {actions ? <div className="product-card__actions">{actions}</div> : null}
    </article>
  )
}

export function OfferCard({
  title,
  description,
  icon,
  price,
  affordable = true,
  action
}: {
  title: string
  description?: string | undefined
  icon: string
  price: ReactNode
  affordable?: boolean | undefined
  action?: ReactNode
}) {
  return (
    <ProductCard
      className={!affordable ? "is-disabled" : ""}
      icon={icon}
      title={title}
      description={description}
      meta={
        <>
          <span>Цена</span>
          {price}
          {!affordable ? <small>Не хватает баланса</small> : null}
        </>
      }
      actions={action}
    />
  )
}
export function AbilityCard({
  title,
  description,
  icon,
  rarity = "COMMON",
  count,
  status,
  action,
  color
}: {
  title: string
  description?: string | undefined
  icon: string
  rarity?: string | undefined
  count?: number | undefined
  status?: string | undefined
  action?: ReactNode
  color?: string | undefined
}) {
  return (
    <ProductCard
      className={`rarity-${rarity.toLocaleLowerCase()}`}
      icon={icon}
      title={`${title}${count && count > 1 ? ` ×${count}` : ""}`}
      description={description}
      accent={color}
      meta={
        <>
          <Badge tone="violet">{rarity}</Badge>
          {status ? <span>{status}</span> : null}
        </>
      }
      actions={action}
    />
  )
}
export function AchievementCard({
  title,
  description,
  icon,
  rarity = "COMMON",
  awarded = false,
  action,
  color
}: {
  title: string
  description?: string | undefined
  icon: string
  rarity?: string | undefined
  awarded?: boolean | undefined
  action?: ReactNode
  color?: string | undefined
}) {
  return (
    <ProductCard
      className={`achievement-product-card rarity-${rarity.toLocaleLowerCase()} ${awarded ? "is-awarded" : ""}`}
      icon={icon}
      title={title}
      description={description}
      accent={color}
      meta={
        <>
          <Badge tone={awarded ? "success" : "neutral"}>{awarded ? "Получено" : "Ещё впереди"}</Badge>
          <span>{rarity}</span>
        </>
      }
      actions={action}
    />
  )
}
